import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const CATEGORIEEN = [
  { key: 'werk',       label: '🛠️ Werk',       rollen: ['hoofdbeheerder','super_admin','bedrijf_admin','werkvoorbereider','manager','monteur'] },
  { key: 'veiligheid', label: '👷 Veiligheid', rollen: ['hoofdbeheerder','super_admin','bedrijf_admin','werkvoorbereider','manager','monteur'] },
  { key: 'personeel',  label: '👤 Personeel',  rollen: ['hoofdbeheerder','super_admin','bedrijf_admin','hr','monteur'] },
  { key: 'klant',      label: '🏢 Klant',      rollen: ['hoofdbeheerder','super_admin','bedrijf_admin','werkvoorbereider','manager'] }
];

const Documenten = () => {
  const { gebruiker } = useAuth();
  const rol = gebruiker?.rol;
  const adminRollen = ['hoofdbeheerder','super_admin','bedrijf_admin'];
  const isAdmin = adminRollen.includes(rol);
  const isMonteur = rol === 'monteur';

  // Toegestane categorieën voor deze gebruiker
  const beschikbareCats = CATEGORIEEN.filter(c => c.rollen.includes(rol));
  const [actieveCat, setActieveCat] = useState(beschikbareCats[0]?.key || 'werk');

  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState([]);
  const [monteurs, setMonteurs] = useState([]);
  const [werkorders, setWerkorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    laadAlles();
    // eslint-disable-next-line
  }, [actieveCat]);

  const laadAlles = async () => {
    setLoading(true);
    try {
      const [docsRes, typesRes, mRes, woRes] = await Promise.all([
        api.get('/documenten?categorie=' + actieveCat),
        api.get('/document-types'),
        api.get('/gebruikers').catch(() => ({ data: { gebruikers: [] } })),
        api.get('/werkorders').catch(() => ({ data: { werkorders: [] } }))
      ]);
      setDocs(docsRes.data.data || []);
      setTypes((typesRes.data.data || []).filter(t => t.actief !== false && t.categorie === actieveCat));
      const allG = mRes.data.gebruikers || mRes.data || [];
      setMonteurs(allG.filter(g => g.actief !== false));
      setWerkorders(woRes.data.werkorders || woRes.data || []);
    } catch (e) {
      console.error('Laad fout:', e);
    } finally { setLoading(false); }
  };

  const verwijderDoc = async (doc) => {
    if (!window.confirm(`Verwijder "${doc.titel}"?`)) return;
    try {
      await api.delete('/documenten/' + doc.id);
      laadAlles();
    } catch (e) {
      alert('Verwijderen mislukt: ' + (e.response?.data?.message || e.message));
    }
  };

  const downloadDoc = (doc) => {
    const token = localStorage.getItem('token');
    const url = (api.defaults.baseURL || '') + '/documenten/' + doc.id + '/download';
    fetch(url, { headers: { 'Authorization': 'Bearer ' + token } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = doc.bestandsnaam_origineel || ('document-' + doc.id);
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(e => alert('Download mislukt: ' + e.message));
  };

  const formatGrootte = (b) => {
    if (!b) return '-';
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return Math.round(b / 1024) + ' KB';
    return (b / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: '#1e293b' }}>📄 Documenten</h1>
        {!isMonteur && (
          <button
            onClick={() => setShowUpload(true)}
            style={{ padding: '10px 18px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
          >+ Document toevoegen</button>
        )}
      </div>

      {/* Categorie tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e5e7eb', marginBottom: 20 }}>
        {beschikbareCats.map(c => (
          <button
            key={c.key}
            onClick={() => setActieveCat(c.key)}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderBottom: actieveCat === c.key ? '3px solid #2563eb' : '3px solid transparent',
              background: 'none',
              color: actieveCat === c.key ? '#2563eb' : '#6b7280',
              fontWeight: actieveCat === c.key ? 600 : 400,
              cursor: 'pointer',
              fontSize: 14
            }}>{c.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Laden...</div>
      ) : docs.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8', background: '#f9fafb', borderRadius: 12 }}>
          Geen documenten in deze categorie.
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600, color: '#374151' }}>Type</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600, color: '#374151' }}>Titel</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600, color: '#374151' }}>Eigenaar</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600, color: '#374151' }}>Werkorder/Klant</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600, color: '#374151' }}>Geldig tot</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600, color: '#374151' }}>Datum</th>
                <th style={{ textAlign: 'right', padding: '12px 16px' }}></th>
              </tr>
            </thead>
            <tbody>
              {docs.map(d => {
                const verlopen = d.geldig_tot && new Date(d.geldig_tot) < new Date();
                return (
                  <tr key={d.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 500 }}>
                        {d.type_naam || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#1e293b', fontWeight: 500 }}>
                      {d.titel}
                      {d.bestandsnaam_origineel && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                          {d.bestandsnaam_origineel} · {formatGrootte(d.bestand_grootte)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#64748b' }}>{d.eigenaar_naam || '-'}</td>
                    <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>
                      {d.werkorder_titel && <div>📋 {d.werkorder_ordernummer || ('W-' + d.werkorder_id)}</div>}
                      {d.klant_naam && <div>🏢 {d.klant_naam}</div>}
                      {!d.werkorder_titel && !d.klant_naam && '-'}
                    </td>
                    <td style={{ padding: '12px 16px', color: verlopen ? '#dc2626' : '#64748b', fontWeight: verlopen ? 600 : 400 }}>
                      {d.geldig_tot ? new Date(d.geldig_tot).toLocaleDateString('nl-NL') : '-'}
                      {verlopen && ' (verlopen)'}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 12 }}>
                      {new Date(d.created_at).toLocaleDateString('nl-NL')}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <button onClick={() => downloadDoc(d)}
                        style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px', marginRight: 6, cursor: 'pointer', fontSize: 12 }}>
                        ⬇️ Download
                      </button>
                      {(isAdmin || (!isMonteur && d.geupload_door_id === gebruiker?.id)) && (
                        <button onClick={() => verwijderDoc(d)}
                          style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}>
                          🗑️
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showUpload && (
        <UploadModal
          categorie={actieveCat}
          types={types}
          monteurs={monteurs}
          werkorders={werkorders}
          onClose={() => setShowUpload(false)}
          onSuccess={() => { setShowUpload(false); laadAlles(); }}
        />
      )}
    </div>
  );
};

// ============================================================
// Upload modal
// ============================================================
const UploadModal = ({ categorie, types, monteurs, werkorders, onClose, onSuccess }) => {
  const [bestand, setBestand] = useState(null);
  const [typeId, setTypeId] = useState('');
  const [titel, setTitel] = useState('');
  const [eigenaarId, setEigenaarId] = useState('');
  const [werkorderId, setWerkorderId] = useState('');
  const [geldigTot, setGeldigTot] = useState('');
  const [opmerking, setOpmerking] = useState('');
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState('');

  const huidigType = types.find(t => String(t.id) === String(typeId));

  const submit = async () => {
    setFout('');
    if (!bestand || !typeId || !titel) {
      setFout('Bestand, type en titel zijn verplicht');
      return;
    }
    if (huidigType?.vereist_werkorder && !werkorderId) {
      setFout('Voor dit type is een werkorder verplicht');
      return;
    }
    if (huidigType?.vereist_geldig_tot && !geldigTot) {
      setFout('Voor dit type is een vervaldatum verplicht');
      return;
    }
    setBezig(true);
    try {
      const fd = new FormData();
      fd.append('bestand', bestand);
      fd.append('type_id', typeId);
      fd.append('titel', titel);
      if (eigenaarId) fd.append('eigenaar_id', eigenaarId);
      if (werkorderId) fd.append('werkorder_id', werkorderId);
      if (geldigTot) fd.append('geldig_tot', geldigTot);
      if (opmerking) fd.append('opmerking', opmerking);
      await api.post('/documenten', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSuccess();
    } catch (e) {
      setFout(e.response?.data?.message || e.message);
    } finally { setBezig(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 16px', fontSize: 20 }}>📄 Document uploaden</h2>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Bestand *</label>
          <input type="file" onChange={e => setBestand(e.target.files[0])}
            accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt"
            style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 8 }} />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Max 25 MB. PDF, afbeelding, Word, Excel, txt.</div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Type *</label>
          <select value={typeId} onChange={e => setTypeId(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8 }}>
            <option value="">— Kies type —</option>
            {types.map(t => <option key={t.id} value={t.id}>{t.naam}</option>)}
          </select>
          {types.length === 0 && (
            <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>
              Geen types beschikbaar voor categorie "{categorie}". Voeg eerst types toe in Instellingen.
            </div>
          )}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Titel *</label>
          <input type="text" value={titel} onChange={e => setTitel(e.target.value)}
            placeholder={huidigType ? huidigType.naam + ' - ' : ''}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }} />
        </div>

        {categorie !== 'klant' && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
              Eigenaar (medewerker) {categorie === 'personeel' && '*'}
            </label>
            <select value={eigenaarId} onChange={e => setEigenaarId(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8 }}>
              <option value="">— Mezelf —</option>
              {monteurs.map(m => <option key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</option>)}
            </select>
          </div>
        )}

        {(huidigType?.vereist_werkorder || categorie === 'klant') && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
              Werkorder {huidigType?.vereist_werkorder && '*'}
            </label>
            <select value={werkorderId} onChange={e => setWerkorderId(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8 }}>
              <option value="">— Geen —</option>
              {werkorders.slice(0, 100).map(w => (
                <option key={w.id} value={w.id}>
                  {w.ordernummer || ('W-' + w.id)} — {w.titel}
                </option>
              ))}
            </select>
          </div>
        )}

        {huidigType?.vereist_geldig_tot && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
              Geldig tot *
            </label>
            <input type="date" value={geldigTot} onChange={e => setGeldigTot(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }} />
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Opmerking</label>
          <textarea value={opmerking} onChange={e => setOpmerking(e.target.value)} rows={2}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }} />
        </div>

        {fout && (
          <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, color: '#dc2626', marginBottom: 14 }}>
            ❌ {fout}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={bezig}
            style={{ padding: '10px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer' }}>
            Annuleren
          </button>
          <button onClick={submit} disabled={bezig || !bestand || !typeId || !titel}
            style={{ padding: '10px 20px', background: bezig ? '#94a3b8' : '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: bezig ? 'not-allowed' : 'pointer' }}>
            {bezig ? 'Uploaden...' : 'Uploaden'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Documenten;
